import React, { ReactNode, useEffect } from 'react';
import {
	View,
	StyleSheet,
	StatusBar,
	useWindowDimensions,
} from 'react-native';
import Animated, {
	useSharedValue,
	useAnimatedStyle,
	withTiming,
} from 'react-native-reanimated';
import {
	Gesture,
	GestureDetector,
	GestureHandlerRootView,
} from 'react-native-gesture-handler';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Theme, ThemeConsumer } from '@rneui/themed';

export default function ResizablePosScreen({ LeftComponent, rightComponent, isorderSummary }: {
	LeftComponent: ReactNode
	rightComponent: ReactNode
	isorderSummary?: true
}) {
	const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = useWindowDimensions();

	// === 1. DETERMINE LAYOUT MODE ===
	const isLarge = SCREEN_WIDTH > 900;
	const isMedium = SCREEN_WIDTH > 600 && SCREEN_WIDTH <= 900;
	const isSmall = SCREEN_WIDTH <= 600;

	const TOTAL_SIZE = isLarge ? SCREEN_WIDTH : SCREEN_HEIGHT;
	const MIN_SIZE_VALUE = isLarge ? isorderSummary ? 0.26 : 0.31 : isMedium ? 0.05 : 0.07;
	const MAX_SIZE_VALUE = isLarge ? isorderSummary ? 0.50 : 0.60 : 0.70;

	const INITIAL_SIZE = isorderSummary ? TOTAL_SIZE * 0.30 : TOTAL_SIZE * 0.45;
	const MIN_SIZE = TOTAL_SIZE * MIN_SIZE_VALUE;
	const MAX_SIZE = TOTAL_SIZE * MAX_SIZE_VALUE;

	const minSizeSV = useSharedValue(MIN_SIZE);
	const maxSizeSV = useSharedValue(MAX_SIZE);
	const panelSize = useSharedValue(INITIAL_SIZE);
	const startSize = useSharedValue(0);

	// 3. THIS IS THE MISSING PART: Sync values when dimensions change
	useEffect(() => {
		minSizeSV.value = MIN_SIZE;
		maxSizeSV.value = MAX_SIZE;
		panelSize.value = withTiming(isLarge ? INITIAL_SIZE : isMedium ? (TOTAL_SIZE * 0.312) : (TOTAL_SIZE * 0.44));

	}, [isLarge, TOTAL_SIZE, INITIAL_SIZE, MIN_SIZE, MAX_SIZE, isMedium, isLarge]);

	const panGesture = Gesture.Pan()
		.onStart(() => {
			startSize.value = panelSize.value;
		})
		.onUpdate((event) => {
			const translation = isLarge ? event.translationX : event.translationY;
			let newSize = startSize.value + translation;

			if (newSize < minSizeSV.value) newSize = minSizeSV.value;
			if (newSize > maxSizeSV.value) newSize = maxSizeSV.value;

			panelSize.value = newSize;
		});

	const firstPanelStyle = useAnimatedStyle(() => {
		return {
			width: isLarge ? panelSize.value : '100%',
			height: isLarge ? '100%' : panelSize.value,
		};
	});

	// Rotate the dragger handle based on orientation
	const draggerStyle = useAnimatedStyle(() => {
		if (isLarge)
			return {
				flexDirection: 'column',
				width: 20,
				height: '100%',
				cursor: 'pointer',
				marginRight: -15,
				marginLeft: -10
			};
		else
			return {
				marginBottom: -10,
				marginTop: -10,
				flexDirection: 'row',
				width: '100%',
				height: 20,
				cursor: 'pointer',
			}
	});

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<GestureHandlerRootView style={{ flex: 1 }}>
						<SafeAreaView style={[styles.container, { flexDirection: isLarge ? 'row' : 'column' }]}>
							<StatusBar barStyle="dark-content" />
							<Animated.View style={[styles.leftPanel, firstPanelStyle, isorderSummary && { backgroundColor: theme?.iceWhite }]}>
								{LeftComponent}
							</Animated.View>


							{/* === DRAGGER HANDLE (NEW GESTURE DETECTOR) === */}
							<GestureDetector gesture={panGesture}>
								<Animated.View style={[styles.draggerHitSlop, draggerStyle]}>
									<View style={[styles.draggerVisualLine, isLarge ? { width: 4, height: "100%", left: -5 } : { width: "100%", height: 4, top: -5 }]}>
										<View style={[styles.draggerHandleIcon, isLarge ? { width: 8, height: 40 } : { height: 8, width: 40 }]} />
									</View>
								</Animated.View>
							</GestureDetector>


							{/* === RIGHT PANEL (CART) === */}

							{rightComponent}

						</SafeAreaView>
					</GestureHandlerRootView>
				)
			}}
		</ThemeConsumer>
	);
}
const style = (theme: Theme) => (
	StyleSheet.create({
		container: { flex: 1, backgroundColor: theme?.lightBackgroundColor },
		leftPanel: { paddingLeft: 12, paddingTop: 16, paddingRight: 12 },

		// Dragger
		draggerHitSlop: {
			zIndex: 10,
			justifyContent: 'center',
			alignItems: 'center'
		},
		draggerVisualLine: {
			backgroundColor: theme?.coolGray,
			alignItems: 'center',
			justifyContent: 'center',
		},
		draggerHandleIcon: {
			backgroundColor: theme.draggerIcon,
			borderRadius: 4,
		},


	}))